import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { track, setAnalyticsUser } from '@/lib/track';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [googleClientId, setGoogleClientId] = useState(null);
  const [heroImages, setHeroImages] = useState([]);
  const [vw, setVw] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));

  // Pull the latest product photos (mix of men's/women's) for the hero fan.
  useEffect(() => {
    axios
      .get(`${API}/products`, { params: { limit: 16 } })
      .then((r) => {
        const imgs = (r.data.items || [])
          .map((p) => p.image_url)
          .filter(Boolean)
          .slice(0, 11);
        setHeroImages(imgs);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Dismiss any lingering toasts
    toast.dismiss();
    
    // Fetch Google Client ID from backend
    const fetchConfig = async () => {
      try {
        const configResponse = await axios.get(`${API}/auth/config`);
        setGoogleClientId(configResponse.data.google_client_id);
      } catch (error) {
        console.error('Failed to fetch auth config:', error);
        toast.error('Failed to load authentication. Please refresh.');
      }
    };
    
    fetchConfig();
    
    // Check if already logged in
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data) {
          navigate('/');
        }
      } catch (e) {
        // Not logged in, show login page
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (!googleClientId) return;

    // Load Google Sign-In script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      // Initialize Google Sign-In
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleCredentialResponse,
          auto_select: false,
        });

        // Render the button
        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button'),
          {
            theme: 'filled_black',
            size: 'large',
            type: 'standard',
            shape: 'rectangular',
            text: 'continue_with',
            logo_alignment: 'left',
            width: 350
          }
        );
      }
    };

    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, [googleClientId]);

  const handleCredentialResponse = async (response) => {
    // Google renders its button in a cross-origin iframe, so the click itself
    // isn't observable — the credential arriving back is the earliest moment we
    // can call this an attempt.
    track('login_start', { method: 'google' });
    try {
      toast.dismiss();
      
      // Send credential to backend
      const result = await axios.post(
        `${API}/auth/google`,
        { credential: response.credential },
        { withCredentials: true }
      );

      if (result.data.success) {
        const user = result.data.user;

        // GA4's recommended pair: sign_up for a brand-new account, login for a
        // returning one. An unfinished profile is our proxy for "first time".
        setAnalyticsUser(user);
        track(user.profile_completed ? 'login' : 'sign_up', { method: 'google' });

        toast.dismiss();
        
        if (!user.profile_completed) {
          toast.success('Welcome! Please complete your profile');
          setTimeout(() => {
            navigate('/onboarding', { state: { user }, replace: true });
          }, 500);
        } else {
          toast.success('Welcome back!');
          setTimeout(() => {
            navigate('/', { state: { user }, replace: true });
          }, 500);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      track('login_failed', {
        method: 'google',
        error_status: error?.response?.status,
      });
      toast.error('Authentication failed. Please try again.');
    }
  };

  // LOCAL DEV ONLY: bypass Google. Button only renders on localhost, and the
  // backend endpoint is inert unless ALLOW_DEV_LOGIN=true (never set in prod).
  const isLocal =
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname);

  const handleDevLogin = async () => {
    try {
      const result = await axios.post(`${API}/auth/dev-login`, {}, { withCredentials: true });
      if (result.data.success) {
        const user = result.data.user;
        navigate(user.profile_completed ? '/' : '/onboarding', { state: { user }, replace: true });
      }
    } catch (e) {
      console.error('Dev login failed:', e);
      toast.error('Dev login failed (is ALLOW_DEV_LOGIN set?)');
    }
  };

  // Responsive hero-fan geometry: a wide, well-spaced arc on desktop (uses the
  // side space) and the compact arc on mobile. `top` keeps it clear of the header.
  const desktop = vw >= 768;
  const fan = desktop
    ? { R: 820, spread: 110, cardW: 104, cardH: 138, boxW: 1360, boxH: 500, top: 40, mt: 380 }
    : { R: 430, spread: 118, cardW: 62, cardH: 82, boxW: 680, boxH: 340, top: 26, mt: 260 };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col items-center px-6 pt-4">
      {/* Hero fan of latest product photos */}
      <div
        className="pointer-events-none select-none absolute left-1/2 -translate-x-1/2 top-0"
        style={{ width: fan.boxW, maxWidth: '150vw', height: fan.boxH }}
        aria-hidden="true"
      >
        {heroImages.map((src, i) => {
          const n = heroImages.length;
          const angle = n > 1 ? -fan.spread / 2 + (fan.spread * i) / (n - 1) : 0;
          return (
            <div
              key={i}
              className="absolute left-1/2 rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/10 bg-[#F0EFEB]"
              style={{
                width: fan.cardW,
                height: fan.cardH,
                top: fan.top,
                transform: `translateX(-50%) rotate(${angle}deg)`,
                transformOrigin: `50% ${fan.R}px`,
              }}
            >
              <img
                src={`${BACKEND_URL}${src}`}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          );
        })}
      </div>

      <div
        className="relative z-10 max-w-md w-full space-y-8 text-center pb-16"
        style={{ marginTop: fan.mt }}
      >
        <div>
          <h1
            className="text-5xl md:text-6xl tracking-tighter mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="login-title"
          >
            North of Six Club
          </h1>
          <p className="text-lg text-[#6B6A68] mb-12">
            A community for tall folks sharing their perfect fits
          </p>
        </div>

        <div className="space-y-6">
          {/* Google Sign-In Button Container */}
          <div className="flex justify-center">
            <div id="google-signin-button" data-testid="google-login-button"></div>
          </div>

          {/* Secured with Google badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-[#6B6A68]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 7V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              Secured with Google
            </span>
          </div>

          <p className="text-sm text-[#6B6A68]">
            Sign in to get new product arrival alerts
          </p>

          {isLocal && (
            <button
              onClick={handleDevLogin}
              className="mt-4 w-full border border-dashed border-[#C9A227] text-[#8a6d1a] py-2 rounded-md text-xs hover:bg-[#faf6e8] transition-colors"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="dev-login-button"
            >
              🔧 Dev Login (local only) - admin
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
